import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, Navigation, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import useProximityRaise from "../hooks/useProximityRaise";

const OFFSET = 64; // room for the sticky progress strip above a section
const NEAR = 2; // collapsed rail shows ±NEAR panels around the active one
const TOP_THRESHOLD = 320; // px scrolled before "Top" appears

// Content panels opt in with data-panel-label (and optionally data-panel-key).
const readSections = () =>
  Array.from(
    document.querySelectorAll("[data-panel-section][data-panel-label]"),
  ).map((el) => ({
    el,
    key: el.dataset.panelKey || el.dataset.panelLabel,
    label: el.dataset.panelLabel,
  }));

/**
 * Panel navigator, on the left or right (`side`).
 * - md+: a slim fixed rail listing the panel names near the current one, fading
 *   out above/below; hovering (or keyboard focus) expands it to the full list.
 *   The active panel is tracked by scroll-spy; clicking smooth-scrolls to it.
 *   A "Top" button appears under the rail once the page is scrolled.
 * - Mobile: hidden rail; a small "jump to" button opens a bottom sheet, plus a
 *   "Top" button when scrolled.
 */
const PanelRail = ({ side = "left" }) => {
  const [sections, setSections] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const keysRef = useRef("");
  const right = side === "right";
  // The rail sits behind the sticky-note layers and rises above them only
  // while the pointer is near it (or it has keyboard focus).
  const railRef = useRef(null);
  const raised = useProximityRaise(railRef, 28);

  // Discover panels; re-scan on DOM changes (panels can be toggled/reordered)
  // but only commit when the set actually changed.
  useEffect(() => {
    let raf = 0;
    const scan = () => {
      raf = 0;
      const next = readSections();
      const sig = next.map((s) => s.key).join("|");
      if (sig !== keysRef.current) {
        keysRef.current = sig;
        setSections(next);
      }
    };
    scan();
    const mo = new MutationObserver(() => {
      if (!raf) raf = requestAnimationFrame(scan);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Scroll-spy + "scrolled" flag (timer-throttled so it also runs in
  // background/throttled tabs where requestAnimationFrame is paused).
  useEffect(() => {
    let timer = 0;
    const update = () => {
      timer = 0;
      setScrolled(window.scrollY > TOP_THRESHOLD);
      if (!sections.length) return;
      const y = window.scrollY + window.innerHeight * 0.35;
      let best = null;
      for (const s of sections) {
        const top = s.el.getBoundingClientRect().top + window.scrollY;
        if (top <= y) best = s;
      }
      if (!best) best = sections[0];
      setActiveKey(best.key);
    };
    const onScroll = () => {
      if (!timer) timer = setTimeout(update, 60);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [sections]);

  // Esc closes the mobile sheet.
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setSheetOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheetOpen]);

  const scrollTo = useCallback((s) => {
    const top = s.el.getBoundingClientRect().top + window.scrollY - OFFSET;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    setSheetOpen(false);
  }, []);
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const activeIndex = Math.max(
    0,
    sections.findIndex((s) => s.key === activeKey),
  );
  const sideCls = right ? "right-2 lg:right-3" : "left-2 lg:left-3";
  const mobileSideCls = right ? "right-3" : "left-3";

  return (
    <>
      {/* Tablet / desktop rail */}
      {sections.length > 0 && (
        <nav
          ref={railRef}
          aria-label="Page sections"
          className={`hidden md:flex flex-col items-center gap-2 fixed ${sideCls} top-1/2 -translate-y-1/2 transition-opacity duration-200 ${
            raised ? "z-[45] opacity-100" : "z-20 opacity-70"
          }`}
          onMouseEnter={() => setExpanded(true)}
          onMouseLeave={() => setExpanded(false)}
          onFocus={() => setExpanded(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setExpanded(false);
          }}
        >
          <motion.div
            layout
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            className="glass rounded-2xl card-shadow py-2 px-1.5"
            style={
              expanded
                ? undefined
                : {
                    WebkitMaskImage:
                      "linear-gradient(to bottom, transparent, black 22%, black 78%, transparent)",
                    maskImage:
                      "linear-gradient(to bottom, transparent, black 22%, black 78%, transparent)",
                  }
            }
          >
            <ul className="flex flex-col gap-0.5">
              {sections.map((s, i) => {
                const active = s.key === activeKey;
                const near = Math.abs(i - activeIndex) <= NEAR;
                if (!expanded && !near) return null;
                return (
                  <li key={s.key}>
                    <button
                      type="button"
                      onClick={() => scrollTo(s)}
                      aria-current={active ? "true" : undefined}
                      title={s.label}
                      className={`flex items-center gap-2 rounded-xl px-2 transition-all ${
                        right ? "flex-row-reverse text-right" : ""
                      } ${expanded ? "py-1.5 w-44" : "py-1 w-32"} ${
                        active
                          ? "bg-cyan-500/20 text-cyan-200"
                          : "text-gray-400 hover:text-cyan-300 hover:bg-gray-700/60"
                      }`}
                    >
                      <span
                        className={`block rounded-full flex-shrink-0 ${
                          active ? "w-2.5 h-2.5 bg-cyan-400" : "w-2 h-2 bg-gray-500"
                        }`}
                      />
                      <span
                        className={`truncate font-semibold flex-1 ${
                          expanded ? "text-xs" : "text-[11px]"
                        }`}
                      >
                        {s.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </motion.div>
          <AnimatePresence>
            {scrolled && (
              <motion.button
                key="top"
                type="button"
                onClick={toTop}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                className="tap glass rounded-full p-2.5 text-cyan-300 card-shadow hover:text-cyan-100"
                aria-label="Back to top"
                title="Back to top"
              >
                <ArrowUp className="w-4 h-4" />
              </motion.button>
            )}
          </AnimatePresence>
        </nav>
      )}

      {/* Mobile: jump-to button (+ Top when scrolled) + bottom sheet */}
      <div className={`md:hidden fixed ${mobileSideCls} bottom-4 z-40 flex flex-col items-center gap-2`}>
        {scrolled && (
          <button
            type="button"
            onClick={toTop}
            className="tap glass rounded-full p-3 text-cyan-300 card-shadow"
            aria-label="Back to top"
            title="Back to top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        )}
        {sections.length > 0 && (
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="tap glass rounded-full p-3 text-cyan-300 card-shadow"
            aria-label="Jump to section"
            title="Jump to section"
          >
            <Navigation className="w-5 h-5" />
          </button>
        )}
      </div>
      <AnimatePresence>
        {sheetOpen && (
          <>
            <motion.div
              key="backdrop"
              className="md:hidden fixed inset-0 z-50 bg-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSheetOpen(false)}
            />
            <motion.div
              key="sheet"
              role="dialog"
              aria-modal="true"
              aria-label="Jump to section"
              className="md:hidden fixed left-0 right-0 bottom-0 z-50 glass rounded-t-3xl p-4"
              style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-cyan-200 font-semibold">Jump to</span>
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  className="tap p-2 text-gray-400 hover:text-cyan-300"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <ul className="grid grid-cols-2 gap-2">
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      toTop();
                      setSheetOpen(false);
                    }}
                    className="tap w-full text-left px-3 py-3 rounded-2xl text-sm font-semibold border-2 bg-gray-700/60 text-gray-300 border-gray-600 flex items-center gap-2"
                  >
                    <ArrowUp className="w-4 h-4" />
                    Top
                  </button>
                </li>
                {sections.map((s) => (
                  <li key={s.key}>
                    <button
                      type="button"
                      onClick={() => scrollTo(s)}
                      className={`tap w-full text-left px-3 py-3 rounded-2xl text-sm font-semibold border-2 ${
                        s.key === activeKey
                          ? "bg-cyan-500/20 text-cyan-200 border-cyan-500/50"
                          : "bg-gray-700/60 text-gray-300 border-gray-600"
                      }`}
                    >
                      {s.label}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default PanelRail;
